import React, { useEffect, useMemo, useState } from "react";
import { FaPlay, FaStar } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import BaseUrl from "../ApiServices/BaseUrl";
import "./FeaturedProjects.css";

const createEmptyProject = () => ({
  key: "",
  video_title: "",
  video_url: "",
  video_cta_label: "",
  video_cta_link: "",
  video_enabled: false
});

const getYouTubeVideoId = (url) => {
  if (!url) return "";

  try {
    const cleaned = String(url).trim();
    const embedMatch = cleaned.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/);
    if (embedMatch?.[1]) return embedMatch[1];

    const watchMatch = cleaned.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
    if (watchMatch?.[1]) return watchMatch[1];

    const shortMatch = cleaned.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/);
    if (shortMatch?.[1]) return shortMatch[1];

    const shortsMatch = cleaned.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/);
    if (shortsMatch?.[1]) return shortsMatch[1];
  } catch (error) {
    console.error("Error parsing YouTube URL:", error);
  }

  return "";
};

const FeaturedProjects = () => {
  const { t } = useTranslation();
  const [content, setContent] = useState({
    section_heading: "",
    section_description: "",
    projects: [createEmptyProject(), createEmptyProject(), createEmptyProject()]
  });
  const [playingVideos, setPlayingVideos] = useState({});

  useEffect(() => {
    let isMounted = true;

    const fetchContent = async () => {
      try {
        const response = await fetch(`${BaseUrl}/api/footer-content`);
        const data = await response.json();
        if (!response.ok || !isMounted) return;

        const projects = (Array.isArray(data.projects) ? data.projects : [])
          .slice(0, 3)
          .map((project, index) => ({
            key: project.key || `project_${index + 1}`,
            video_title: project.video_title || "",
            video_url: project.video_url || "",
            video_cta_label: project.video_cta_label || "",
            video_cta_link: project.video_cta_link || "",
            video_enabled: project.video_enabled === true
          }));

        while (projects.length < 3) {
          projects.push(createEmptyProject());
        }

        setContent({
          section_heading: data.section_heading || "",
          section_description: data.section_description || "",
          projects
        });
      } catch (error) {
        console.error("Error fetching featured projects:", error);
      }
    };

    fetchContent();

    return () => {
      isMounted = false;
    };
  }, []);

  const projectCards = useMemo(() => {
    return content.projects
      .map((project, index) => {
        const videoId = getYouTubeVideoId(project.video_url);
        const hasAnyContent =
          project.video_enabled &&
          (videoId || project.video_title || project.video_cta_label);

        return {
          ...project,
          key: project.key || `project_${index + 1}`,
          index,
          videoId,
          videoThumbnail: videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : "",
          videoEmbedUrl: videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : "",
          hasAnyContent
        };
      })
      .filter((project) => project.hasAnyContent);
  }, [content.projects]);

  if (projectCards.length === 0) return null;

  return (
    <section className="featured-projects-section">
      <div className="featured-projects-showcase">
        <div className="featured-projects-header">
          <span className="featured-projects-eyebrow">
            <FaStar />
            {t("featured_projects.eyebrow")}
          </span>
          {content.section_heading && (
            <h3 className="featured-projects-title">{content.section_heading}</h3>
          )}
          {content.section_description && (
            <p className="featured-projects-description">
              {content.section_description}
            </p>
          )}
        </div>

        <div
          className={`featured-projects-grid featured-projects-grid-${Math.min(
            projectCards.length,
            3
          )}`}
        >
          {projectCards.map((project) => (
            <article key={project.key} className="featured-projects-card">
              {project.video_title && (
                <h4 className="featured-projects-card-title">{project.video_title}</h4>
              )}

              {project.videoId && playingVideos[project.key] ? (
                <div className="featured-projects-video-frame-wrap">
                  <iframe
                    className="featured-projects-video-frame"
                    src={project.videoEmbedUrl}
                    title={`${project.video_title || "Featured"} video`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              ) : project.videoId ? (
                <button
                  type="button"
                  className="featured-projects-video-preview"
                  onClick={() =>
                    setPlayingVideos((prev) => ({
                      ...prev,
                      [project.key]: true
                    }))
                  }
                  aria-label={`Play ${project.video_title || "featured"} video`}
                >
                  <img
                    src={project.videoThumbnail}
                    alt={`${project.video_title || "Featured"} preview`}
                    className="featured-projects-video-thumbnail"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="featured-projects-video-play">
                    <FaPlay />
                  </span>
                </button>
              ) : (
                <div className="featured-projects-video-unavailable">
                  {t("featured_projects.video_unavailable")}
                </div>
              )}

              {project.video_cta_label && project.video_cta_link && (
                <a
                  href={project.video_cta_link}
                  className="featured-projects-cta"
                  target={project.video_cta_link.startsWith("http") ? "_blank" : undefined}
                  rel={project.video_cta_link.startsWith("http") ? "noopener noreferrer" : undefined}
                >
                  {project.video_cta_label}
                </a>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;
